'use client';

import { isMac } from '@heidi/utils';
import { useAtomValue } from 'jotai';
import {
  DICTATION_HOTKEY_DEFAULTS,
  getUserPlatform,
  type DictationHotkeyPlatform,
} from '../../onboarding/hotkey/utils';
import {
  dictateOnboardingDevPlatformOverrideAtom,
  isDictateOnboardingDevOverridesEnabled,
} from '../../onboarding/keyboard/dictateOnboardingDevAtoms';

import { getDictationBubblePorts } from '../../bubble/bubble-ports';
import { useDictateOnboardingContext } from '../context/DictateOnboardingContext';

/**
 * Platform for dictate onboarding previews. Respects dev-tools Mac/Windows override
 * in non-production builds; otherwise matches the runtime Mac check.
 */
export function useDictateOnboardingIsMac(): boolean {
  const platformOverride = useAtomValue(dictateOnboardingDevPlatformOverrideAtom);
  if (!isDictateOnboardingDevOverridesEnabled()) {
    return isMac;
  }
  if (platformOverride === 'mac') return true;
  if (platformOverride === 'windows') return false;
  return isMac;
}

/**
 * Hotkey chord for onboarding UI. When the keyboard preview OS differs from the
 * runtime (e.g. Mac dev previewing Windows layout), use that preview OS default
 * so the illustration highlights Right Ctrl on Windows, not Fn.
 */
export function useDictateOnboardingHotkey(): { hotkey: string[] } {
  const isMacPreview = useDictateOnboardingIsMac();
  const { variant, selectedHotkey, currentStep } = useDictateOnboardingContext();
  const { hotkey } = getDictationBubblePorts().useDictationHotkey();
  const runtimePlatform = getUserPlatform();
  const previewPlatform: DictationHotkeyPlatform = isMacPreview ? 'mac' : 'windows';

  // Full onboarding starts before settings exist, so key-confirm validates the
  // platform default. Authenticated lite validates the user's configured key.
  if (currentStep === 'key-confirm' && variant !== 'lite') {
    return { hotkey: [...DICTATION_HOTKEY_DEFAULTS[previewPlatform]] };
  }

  if (selectedHotkey) {
    return { hotkey: selectedHotkey };
  }

  if (runtimePlatform !== previewPlatform) {
    return { hotkey: [...DICTATION_HOTKEY_DEFAULTS[previewPlatform]] };
  }
  return { hotkey };
}
